/**
 * Neutral advisory chip for the live SED source-role review (Stage 2).
 *
 * Deliberately NOT warning/error styled and never counted in readiness,
 * preflight, completeness, anomaly or group aggregates: an automated second
 * reader suggested checking the source basis of an adopted document value.
 * It is not a finding and changes nothing; the user decides through the
 * existing accept / amend flow, and any such decision clears the chip.
 */
import React from 'react';
import type { BoundSourceRoleFlag } from '../../lib/sed/sourceRoleReviewBinding';

export const SOURCE_BASIS_REVIEW_CHIP_TEXT = 'Source basis: review suggested (automated second reader)';

interface SourceBasisReviewChipProps {
  flag: BoundSourceRoleFlag;
  /** Optional field label, used when the chip is not beside its own row. */
  fieldLabel?: string;
  testId?: string;
}

export function SourceBasisReviewChip({ flag, fieldLabel, testId }: SourceBasisReviewChipProps) {
  // A chip beside its own row can be resolved there (accepting or amending
  // the value clears it); a chip listed for a leaf without a row cannot.
  const tooltip = `${flag.meaning} Suggested by an automated second reader; advisory only, not a finding. `
    + (fieldLabel
      ? 'Nothing was changed. Check the source passage.'
      : 'Nothing was changed. Check the source passage; accepting or amending the value clears this suggestion.');
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 type-meta rounded-md border border-border text-text-secondary"
      title={tooltip}
      aria-label={`${fieldLabel ? `${fieldLabel}: ` : ''}${SOURCE_BASIS_REVIEW_CHIP_TEXT}. ${flag.meaning}`}
      data-testid={testId ?? `source-basis-review-chip-${flag.key}`}
      data-source-role-label={flag.label}
    >
      {fieldLabel ? `${fieldLabel} · ` : ''}{SOURCE_BASIS_REVIEW_CHIP_TEXT}
    </span>
  );
}
